import { Button, CheckBox, InfoBar } from "@etframework/ui.components";
import { SkeletonComponent } from "@syncfusion/ej2-react-notifications";
import moment from "moment";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import useOrdinal from "../../hooks/useOrdinal";
import Enums from "../../types/enums";
import { safeCreateDate } from "../../utils/helpers";


// Constants for months and quarters
const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec"
];
const QUARTERS = [
  "firstQuarter",
  "secondQuarter",
  "thirdQuarter",
  "fourthQuarter"
];

export const DueDateRule = forwardRef(({ initialValue, id }, ref) => {
  const { t, i18n } = useTranslation(["CreateTaskModal"]);
  const { getMonthWord } = useOrdinal(i18n.language);
  moment.locale(i18n.language);
  const componentRef = useRef();

  const [showError, setShowError] = useState(false);
  const [dueDateRuleInfo, setDueDateRuleInfo] = useState(
    initialValue?.DueDateRule ?? null
  );
  const [dueDateCalculation, setDueDateCalculation] = useState({});
  const [checkedCheckboxes, setCheckedCheckboxes] = useState(
    initialValue?.CheckedValues ?? {
      quarterly: QUARTERS.reduce((acc, quarter) => {
        acc[quarter] = false;
        return acc;
      }, {}),
      monthly: MONTHS.reduce((acc, month) => {
        acc[month] = false;
        return acc;
      }, {})
    }
  );

  const handleOnChange = (e, id, frequency) => {
    setShowError(false);
    setCheckedCheckboxes((prev) => {
      var updated = { ...prev };
      if (frequency === "quarterly") {
        updated.quarterly = {
          ...prev.quarterly,
          [id]: e.checked
        };

        updated.monthly = MONTHS.reduce((acc, month) => {
          acc[month] = false;
          return acc;
        }, {});
      } else {
        updated.monthly = {
          ...prev.monthly,
          [id]: e.checked
        };

        updated.quarterly = QUARTERS.reduce((acc, quarter) => {
          acc[quarter] = false;
          return acc;
        }, {});
      }
      return updated;
    });
  };

  const modifyCheckBoxes = (value, frequency) => {
    setCheckedCheckboxes((prev) => {
      let updated = { ...prev };
      if (frequency === "quarterly") {
        updated.quarterly = QUARTERS.reduce((acc, quarter) => {
          acc[quarter] = value;
          return acc;
        }, {});
      } else {
        updated.monthly = MONTHS.reduce((acc, month) => {
          acc[month] = value;
          return acc;
        }, {});
      }
      return updated;
    });
  };

  const QuarterlyFrequency = () => {
    return (
      <div className="py-1">
        {QUARTERS.map((key, index) => (
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            key={index}
            className="mb-2"
          >
            <CheckBox
              id={`${key}-${index}`}
              checked={checkedCheckboxes.quarterly[key]}
              label={t(key)}
              change={(e) => handleOnChange(e, key, "quarterly")}
            />
            <span>
              {dueDateCalculation?.Quarterly?.[index] ? (
                getQuarterlyDateString(index)
              ) : (
                <SkeletonComponent height={"1em"} width={50} />
              )}
            </span>
          </div>
        ))}
        {showError && (
          <div className="mt-1">
            <small className="error-message">
              <i className="fa-regular fa-circle-exclamation"></i>{" "}
              {t("QuarterErrorMsg")}
            </small>
          </div>
        )}
      </div>
    );
  };

  const MonthlyFrequency = () => {
    const startIndex = ((dueDateRuleInfo?.Month ?? 1) - 1) % 12;

    const reorderedMonthKeys = [
      ...MONTHS.slice(startIndex),
      ...MONTHS.slice(0, startIndex)
    ];

    return (
      <>
        <div
          className="py-1"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "0.5rem"
          }}
        >
          {reorderedMonthKeys.map((key, index) => (
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              key={index}
            >
              <CheckBox
                id={`${key}-${index}`}
                checked={checkedCheckboxes.monthly[key]}
                change={(e) => handleOnChange(e, key, "monthly")}
              />
              <span>{t(key)}</span>
            </div>
          ))}

          {showError && (
            <div className="mt-1">
              <small className="error-message">
                <i className="fa-regular fa-circle-exclamation"></i>{" "}
                {t("MonthlyErrorMsg")}
              </small>
            </div>
          )}
        </div>
        <div className="my-2">
          <InfoBar
            message={t("MonthlyTaskInformation")}
            variant="information"
          />
        </div>
      </>
    );
  };

  const getQuarterlyDateString = (quarter) => {
    const date = moment(dueDateCalculation?.Quarterly?.[quarter]).locale(
      i18n.language
    );

    const month =
      date.format("MMMM").charAt(0).toUpperCase() +
      date.format("MMMM").slice(1);
    return `${month} ${date.format("DD")}`;
  };

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => componentRef.current.getValue(),
      setDueDateRuleInfo,
      setDueDateCalculation,
      setShowError: (value) => setShowError(value),
      hasDueDateRuleInfo: () => dueDateRuleInfo != null,
      getDueDateRuleInfo: () => dueDateRuleInfo,
      getCheckedValues: () => checkedCheckboxes
    }),
    [componentRef, checkedCheckboxes, dueDateRuleInfo, setShowError]
  );

  if (
    dueDateRuleInfo === null ||
    dueDateRuleInfo.DateFrequency === Enums.DateFrequency.Annual
  )
    return null;

  return (
    <div id={id} className="et-due-date-rule bg-et-lightest-gray">
      <div className="p-3 extension-rule space-y-2">
        <div className="fs-12">
          <Trans components={{ b: <strong /> }}>
            {dueDateRuleInfo.DateFrequency === Enums.DateFrequency.Quarterly
              ? t("DueDateRuleQuarterlyTitle", { day: dueDateRuleInfo?.Day })
              : t("DueDateRuleMonthlyTitle", {
                  month: getMonthWord(dueDateRuleInfo?.Month ?? 2),
                  day: dueDateRuleInfo?.Day
                })}
          </Trans>
        </div>
        <div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="fw-700 fs-12 text-nowrap me-2">
              {t("DueDateRuleHeader")}
            </div>
            <div className="horizontal-divider"></div>
            <div className="d-flex justify-content-end align-items-center">
              <Button
                className="btn-tertiary w-auto text-nowrap rule-btns"
                text={t("ClearAll")}
                onClick={() =>
                  modifyCheckBoxes(
                    false,
                    dueDateRuleInfo.DateFrequency ===
                      Enums.DateFrequency.Quarterly
                      ? "quarterly"
                      : "monthly"
                  )
                }
              />
              <div className="vertical-divider"></div>
              <Button
                className="btn-tertiary w-auto text-nowrap rule-btns"
                text={t("SelectAll")}
                onClick={() =>
                  modifyCheckBoxes(
                    true,
                    dueDateRuleInfo.DateFrequency ===
                      Enums.DateFrequency.Quarterly
                      ? "quarterly"
                      : "monthly"
                  )
                }
              />
            </div>
          </div>
          <div className="mt-1">
            {dueDateRuleInfo.DateFrequency === Enums.DateFrequency.Quarterly ? (
              <QuarterlyFrequency />
            ) : (
              <MonthlyFrequency />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
